import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MfaCodeStep, MfaEnrollStep } from './mfa-steps';
import { AdminIcon, Btn, Crumb, Field, Form, Input, Note, initials } from '../admin-kit';
import { useSession } from '../../shared/session';
import { AuthFrame, AuthTitle } from './auth-frame';

/**
 * Aanmelden met e-mail en wachtwoord (beheer en kantoorpersoneel), sinds ACC-1 zonder bedrijfscode.
 * Zit het adres in meer dan één bedrijf, dan volgt "Waar wil je naartoe?" - pas na het wachtwoord.
 *
 * Spec 01 T1 (aanmelden), T2 (fout) en T3 (kies een bedrijf).
 */
export function AdminLoginPage() {
  const { t } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [failed, setFailed] = useState(false);
  const [choice, setChoice] = useState<{
    choose: { tenantCode: string; name: string }[];
    chooseToken: string;
  } | null>(null);
  // FBK-12: de code uit de authenticator-app, of eerst 2FA instellen.
  const [mfa, setMfa] = useState<{ tenantCode?: string; failed: boolean } | null>(null);
  const [enrollToken, setEnrollToken] = useState<string | null>(null);

  /** Een stap van de aanmelding verwerken: klaar, kiezen, code vragen of inschrijven. */
  const next = (result: Awaited<ReturnType<typeof session.login>>, tenantCode?: string): void => {
    if (!result) {
      void navigate({ to: '/admin' });
      return;
    }
    if ('mfa' in result) {
      setMfa({ tenantCode, failed: mfa !== null });
      return;
    }
    if ('enrollToken' in result) {
      setEnrollToken(result.enrollToken);
      return;
    }
    setChoice(result);
  };

  const submitCode = async (second: { totpCode?: string; recoveryCode?: string }) => {
    try {
      // Met een gekozen bedrijf opnieuw via de keuze is niet meer mogelijk (het keuzetoken is
      // eenmalig); daarom altijd opnieuw met e-mail, wachtwoord en de bedrijfscode.
      next(
        await session.login({
          email: email.trim(),
          password,
          tenantCode: mfa?.tenantCode,
          ...second,
        }),
        mfa?.tenantCode,
      );
    } catch {
      setMfa((current) => (current ? { ...current, failed: true } : current));
    }
  };

  const choose = async (tenantCode: string) => {
    if (!choice) return;
    try {
      const result = await session.loginChoose(choice.chooseToken, tenantCode);
      setChoice(null);
      next(result, tenantCode);
    } catch {
      // Het keuzetoken is kort geldig en eenmalig: bij een fout opnieuw beginnen.
      setChoice(null);
      setPassword('');
      setFailed(true);
    }
  };

  const submit = async () => {
    setFailed(false);
    try {
      next(await session.login({ email: email.trim(), password }));
    } catch {
      // Eén melding voor elke oorzaak: anders is dit endpoint een manier om te
      // achterhalen welke bedrijven en e-mailadressen bestaan. Het wachtwoord gaat leeg (T2).
      setPassword('');
      setFailed(true);
    }
  };

  if (enrollToken) {
    return (
      <AuthFrame>
        <AuthTitle>{t('mfa.enrollTitle')}</AuthTitle>
        <MfaEnrollStep
          enrollToken={enrollToken}
          onDone={() => void navigate({ to: '/admin' })}
          onExpired={() => {
            setEnrollToken(null);
            setPassword('');
          }}
        />
      </AuthFrame>
    );
  }

  if (mfa) {
    return (
      <AuthFrame>
        <Crumb
          crumb={{
            label: t('login.chooseBack'),
            onClick: () => {
              setMfa(null);
              setPassword('');
            },
          }}
        />
        <AuthTitle>{t('mfa.title')}</AuthTitle>
        <MfaCodeStep
          busy={session.loading}
          failed={mfa.failed}
          onSubmit={(second) => void submitCode(second)}
        />
      </AuthFrame>
    );
  }

  if (choice) {
    return (
      <AuthFrame>
        <Crumb crumb={{ label: t('login.chooseBack'), onClick: () => setChoice(null) }} />
        <AuthTitle>{t('login.chooseTitle')}</AuthTitle>
        <p className="a-muted">{t('beheer.toegang.login.chooseText')}</p>
        {/*
          Per bedrijf naam en code. Een rol geeft de API hier niet mee: die hoort bij het bedrijf,
          en dat is pas gekozen na deze stap.
        */}
        <div className="a-orgpick" data-testid="login-choose">
          {choice.choose.map((item) => (
            <button
              key={item.tenantCode}
              type="button"
              className="a-orgbtn"
              disabled={session.loading}
              aria-busy={session.loading || undefined}
              onClick={() => void choose(item.tenantCode)}
            >
              <span className="a-orgtile" aria-hidden="true">
                {initials(item.name)}
              </span>
              <span style={{ flex: 1 }}>
                <span className="a-name" style={{ display: 'block' }}>
                  {item.name}
                </span>
                <span className="a-meta">{item.tenantCode}</span>
              </span>
              <AdminIcon name="chevronRight" size={18} style={{ color: 'var(--a-muted)' }} />
            </button>
          ))}
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <AuthTitle>{t('login.adminTitle')}</AuthTitle>
      {failed ? (
        <Note tone="danger" role="alert" lead={t('beheer.toegang.login.failedLead')}>
          {t('beheer.toegang.login.failedText')}
        </Note>
      ) : null}
      <Form onSubmit={() => void submit()}>
        <Field label={t('login.email')}>
          {(props) => (
            <Input
              {...props}
              name="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
            />
          )}
        </Field>
        <Field label={t('login.password')}>
          {(props) => (
            <Input
              {...props}
              name="password"
              type="password"
              autoComplete="current-password"
              placeholder={failed ? t('login.password') : undefined}
              aria-invalid={failed || undefined}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
            />
          )}
        </Field>
        <Btn
          type="submit"
          size="lg"
          block
          disabled={session.loading}
          aria-busy={session.loading || undefined}
        >
          {t('login.submit')}
        </Btn>
      </Form>
      <div className="a-auth-links">
        <Link to="/admin/forgot-password" className="a-link">
          {t('login.forgot')}
        </Link>
        <Link to="/master/login" className="a-link">
          {t('login.toPlatform')}
        </Link>
      </div>
    </AuthFrame>
  );
}

/**
 * Aanmelden als platformbeheerder van UP2D8 (spec 01 T4).
 *
 * Twee stappen: eerst wachtwoord, dan de code uit de authenticator-app. Het codeveld
 * verschijnt pas nadat het wachtwoord klopte — wie het wachtwoord niet heeft, komt zo
 * niet te weten dat er een tweede factor is. De mockup toont de drie velden samen; dat is
 * hier de tweede stap.
 */
export function PlatformLoginPage() {
  const { t } = useTranslation();
  // De productnaam van het platformmerk in de kop (MRK-2); met UP2D8 de bestaande tekst.
  const session = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [stage, setStage] = useState<'password' | 'totp'>('password');
  const [error, setError] = useState<{ tone: 'info' | 'danger'; text: string } | null>(null);
  // Nog geen 2FA (FBK-12): eerst de authenticator-app koppelen.
  const [enrollToken, setEnrollToken] = useState<string | null>(null);
  const [recovery, setRecovery] = useState(false);

  const submit = async () => {
    setError(null);

    let result: Awaited<ReturnType<typeof session.loginPlatform>>;
    try {
      result = await session.loginPlatform({
        email: email.trim(),
        password,
        totpCode: stage === 'totp' && !recovery ? totpCode.trim() : undefined,
        recoveryCode: stage === 'totp' && recovery ? totpCode.trim() : undefined,
      });
    } catch {
      // Geen netwerk: de aanroep gooit. Dan dezelfde melding in plaats van stilte.
      result = 'failed';
    }

    if (typeof result === 'object') {
      setEnrollToken(result.enrollToken);
      return;
    }
    if (result === 'ok') {
      void navigate({ to: '/master' });
      return;
    }
    if (result === 'mfa') {
      // Geen fout: de volgende stap. Daarom blauw en niet rood.
      setStage('totp');
      setError({ tone: 'info', text: t('login.totpNeeded') });
      return;
    }
    setError({
      tone: 'danger',
      text: stage === 'totp' ? t('login.totpFailed') : t('beheer.toegang.platform.failed'),
    });
  };

  if (enrollToken) {
    return (
      <AuthFrame platform>
        <AuthTitle>{t('mfa.enrollTitle')}</AuthTitle>
        <MfaEnrollStep
          enrollToken={enrollToken}
          onDone={() => void navigate({ to: '/master' })}
          onExpired={() => {
            setEnrollToken(null);
            setPassword('');
          }}
        />
      </AuthFrame>
    );
  }

  return (
    <AuthFrame platform>
      <AuthTitle>{t('beheer.toegang.platform.title')}</AuthTitle>
      <Note tone="info">{t('beheer.toegang.platform.only')}</Note>
      {error ? (
        <Note tone={error.tone} role="alert">
          {error.text}
        </Note>
      ) : null}
      <Form onSubmit={() => void submit()}>
        <Field label={t('login.email')}>
          {(props) => (
            <Input
              {...props}
              name="email"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              readOnly={stage === 'totp'}
              required
            />
          )}
        </Field>
        <Field label={t('login.password')}>
          {(props) => (
            <Input
              {...props}
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              readOnly={stage === 'totp'}
              required
            />
          )}
        </Field>
        {stage === 'totp' ? (
          <Field
            label={t('beheer.toegang.platform.totp')}
            hint={t('beheer.toegang.platform.totpHint')}
          >
            {(props) => (
              <Input
                {...props}
                name="totpCode"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder={t('beheer.toegang.platform.totpPlaceholder')}
                maxLength={6}
                autoFocus
                value={totpCode}
                onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, ''))}
                required
              />
            )}
          </Field>
        ) : null}
        {stage === 'totp' ? (
          <button type="button" className="a-link" onClick={() => setRecovery(!recovery)}>
            {recovery ? t('mfa.useApp') : t('mfa.useRecovery')}
          </button>
        ) : null}
        <Btn
          type="submit"
          size="lg"
          block
          disabled={session.loading}
          aria-busy={session.loading || undefined}
        >
          {t('login.submit')}
        </Btn>
      </Form>
      <div className="a-auth-links">
        <Link to="/admin/login" className="a-link">
          {t('login.toAdmin')}
        </Link>
      </div>
    </AuthFrame>
  );
}
